import { supabase } from '../lib/supabase'
import type { FileNode, FileVersion } from '../types/agentFiles.types'

export const agentFilesService = {
    /**
     * Criar estrutura de arquivos para novo agente
     */
    async createAgentStructure(
        agentId: string,
        handle: string,
        templateName: string = 'default'
    ): Promise<void> {
        try {
            // Carregar template
            const { templateService } = await import('./templateService')
            const templateFiles = await templateService.getTemplateFiles(templateName)

            console.log(`Creating agent structure for ${handle} using template ${templateName}`)
            console.log(`Template has ${templateFiles.length} files`)

            // Criar cada arquivo do template
            for (const templateFile of templateFiles) {
                // Personalizar conteúdo com informações do agente
                let content = templateFile.content
                content = content.replace(/\[Será preenchido com o nome do agente\]/g, handle)
                content = content.replace(/\[Data de criação\]/g, new Date().toLocaleDateString('pt-BR'))

                await this.writeFile(
                    agentId,
                    templateFile.path,
                    content,
                    'Initial agent creation',
                    { type: 'system', id: 'system' }
                )

                console.log(`✅ Created file: ${templateFile.path}`)
            }

            console.log(`✅ Agent structure created for ${handle}`)
        } catch (error) {
            console.error('Error creating agent structure:', error)
            throw error
        }
    },

    /**
     * Ler arquivo
     */
    async readFile(
        agentId: string,
        filePath: string,
        version?: number
    ): Promise<string> {
        try {
            // 1. Buscar metadados do arquivo
            const { data: fileData, error: fileError } = await supabase
                .from('agent_files')
                .select('*')
                .eq('agent_id', agentId)
                .eq('file_path', filePath)
                .single()

            if (fileError || !fileData) {
                throw new Error(`File not found: ${filePath}`)
            }

            // 2. Determinar qual versão ler
            let storagePath = fileData.storage_path
            if (version) {
                const { data: versionData } = await supabase
                    .from('agent_file_versions')
                    .select('storage_path')
                    .eq('file_id', fileData.id)
                    .eq('version', version)
                    .single()

                if (versionData) {
                    storagePath = versionData.storage_path
                }
            }

            // 3. Ler do Supabase Storage
            const { data, error } = await supabase.storage
                .from('agent-files')
                .download(storagePath)

            if (error) throw error

            const content = await data.text()
            return content
        } catch (error) {
            console.error('Error reading file:', error)
            throw error
        }
    },

    /**
     * Escrever arquivo
     */
    async writeFile(
        agentId: string,
        filePath: string,
        content: string,
        changeMessage?: string,
        changedBy?: { type: 'user' | 'agent' | 'system', id: string }
    ): Promise<FileVersion> {
        try {
            // 1. Verificar se arquivo já existe
            const { data: existingFile } = await supabase
                .from('agent_files')
                .select('*')
                .eq('agent_id', agentId)
                .eq('file_path', filePath)
                .single()

            const newVersion = existingFile ? existingFile.current_version + 1 : 1
            const storagePath = `agent-${agentId}/v${newVersion}/${filePath}`

            // 2. Upload para Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from('agent-files')
                .upload(storagePath, content, {
                    contentType: 'text/markdown',
                    upsert: true
                })

            if (uploadError) throw uploadError

            // 3. Atualizar ou criar registro no banco
            if (existingFile) {
                // Atualizar arquivo existente
                const { error: updateError } = await supabase
                    .from('agent_files')
                    .update({
                        storage_path: storagePath,
                        current_version: newVersion,
                        updated_at: new Date().toISOString(),
                        updated_by: changedBy?.type === 'user' ? changedBy.id : null
                    })
                    .eq('id', existingFile.id)

                if (updateError) throw updateError

                // Criar registro de versão
                const { data: versionData, error: versionError } = await supabase
                    .from('agent_file_versions')
                    .insert({
                        file_id: existingFile.id,
                        version: newVersion,
                        storage_path: storagePath,
                        content_hash: await this.hashContent(content),
                        changed_by_type: changedBy?.type || 'user',
                        changed_by_id: changedBy?.type === 'system' ? null : changedBy?.id,
                        change_message: changeMessage
                    })
                    .select()
                    .single()

                if (versionError) throw versionError
                return mapDBVersionToFileVersion(versionData)
            } else {
                // Criar novo arquivo
                const { data: newFile, error: createError } = await supabase
                    .from('agent_files')
                    .insert({
                        agent_id: agentId,
                        file_path: filePath,
                        storage_path: storagePath,
                        file_type: this.getFileType(filePath),
                        current_version: 1,
                        created_by: changedBy?.type === 'user' ? changedBy.id : null
                    })
                    .select()
                    .single()

                if (createError) throw createError

                // Criar primeira versão
                const { data: versionData, error: versionError } = await supabase
                    .from('agent_file_versions')
                    .insert({
                        file_id: newFile.id,
                        version: 1,
                        storage_path: storagePath,
                        content_hash: await this.hashContent(content),
                        changed_by_type: changedBy?.type || 'system',
                        changed_by_id: changedBy?.type === 'system' ? null : changedBy?.id,
                        change_message: changeMessage || 'Initial version'
                    })
                    .select()
                    .single()

                if (versionError) throw versionError
                return mapDBVersionToFileVersion(versionData)
            }
        } catch (error) {
            console.error('Error writing file:', error)
            throw error
        } finally {
            // 🔍 Re-index file in RAG after successful write
            try {
                const { data: { session } } = await supabase.auth.getSession()
                if (session?.access_token) {
                    const reindexResponse = await fetch(
                        `${import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'}/api/v1/agents/${agentId}/files/reindex?file_path=${encodeURIComponent(filePath)}`,
                        {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${session.access_token}`
                            }
                        }
                    )

                    if (reindexResponse.ok) {
                        const result = await reindexResponse.json()
                        console.log(`✅ RAG re-indexed ${filePath}: ${result.chunks_deleted} deleted, ${result.chunks_created} created`)
                    } else {
                        console.warn(`⚠️ RAG re-indexing failed for ${filePath}:`, await reindexResponse.text())
                    }

                    // 🔄 Clear agent cache to ensure backend reads latest version
                    try {
                        const reloadResponse = await fetch(
                            `${import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'}/api/v1/agents/${agentId}/reload`,
                            {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${session.access_token}`
                                }
                            }
                        )

                        if (reloadResponse.ok) {
                            console.log(`✅ Agent cache cleared for ${agentId}`)
                        } else {
                            console.warn(`⚠️ Cache clearing failed for ${agentId}:`, await reloadResponse.text())
                        }
                    } catch (cacheError) {
                        console.error(`Cache clearing error for ${agentId}:`, cacheError)
                        // Don't fail the operation if cache clearing fails
                    }
                }
            } catch (reindexError) {
                console.error(`RAG re-indexing error for ${filePath}:`, reindexError)
                // Don't fail the file write operation if re-indexing fails
            }
        }
    },

    /**
     * Listar arquivos do agente
     */
    async listFiles(
        agentId: string
    ): Promise<FileNode[]> {
        try {
            const { data, error } = await supabase
                .from('agent_files')
                .select('*')
                .eq('agent_id', agentId)

            if (error) throw error

            // Converter para estrutura de árvore
            const files: FileNode[] = []
            const fileMap = new Map<string, FileNode>()

            for (const file of data || []) {
                const parts = file.file_path.split('/')
                let currentPath = ''

                for (let i = 0; i < parts.length; i++) {
                    const part = parts[i]
                    const parentPath = currentPath
                    currentPath = currentPath ? `${currentPath}/${part}` : part

                    if (!fileMap.has(currentPath)) {
                        const isFile = i === parts.length - 1
                        const node: FileNode = {
                            name: part,
                            path: currentPath,
                            type: isFile ? 'file' : 'directory',
                            version: isFile ? file.current_version : undefined,
                            lastModified: isFile ? new Date(file.updated_at) : undefined,
                            children: isFile ? undefined : []
                        }

                        fileMap.set(currentPath, node)

                        if (parentPath) {
                            const parent = fileMap.get(parentPath)
                            if (parent && parent.children) {
                                parent.children.push(node)
                            }
                        } else {
                            files.push(node)
                        }
                    }
                }
            }

            return files
        } catch (error) {
            console.error('Error listing files:', error)
            throw error
        }
    },

    /**
     * Deletar arquivo
     */
    async deleteFile(agentId: string, filePath: string): Promise<void> {
        try {
            const { data: fileData } = await supabase
                .from('agent_files')
                .select('id, storage_path')
                .eq('agent_id', agentId)
                .eq('file_path', filePath)
                .single()

            if (!fileData) {
                throw new Error(`File not found: ${filePath}`)
            }

            // Deletar do storage (todas as versões)
            const { error: storageError } = await supabase.storage
                .from('agent-files')
                .remove([fileData.storage_path])

            if (storageError) console.error('Storage delete error:', storageError)

            // Deletar do banco (cascade vai deletar versões e logs)
            const { error: dbError } = await supabase
                .from('agent_files')
                .delete()
                .eq('id', fileData.id)

            if (dbError) throw dbError
        } catch (error) {
            console.error('Error deleting file:', error)
            throw error
        }
    },

    /**
     * Obter histórico de versões
     */
    async getVersionHistory(
        agentId: string,
        filePath: string
    ): Promise<FileVersion[]> {
        try {
            const { data: fileData } = await supabase
                .from('agent_files')
                .select('id')
                .eq('agent_id', agentId)
                .eq('file_path', filePath)
                .single()

            if (!fileData) {
                throw new Error(`File not found: ${filePath}`)
            }

            const { data, error } = await supabase
                .from('agent_file_versions')
                .select('*')
                .eq('file_id', fileData.id)
                .order('version', { ascending: false })

            if (error) throw error
            return (data || []).map(mapDBVersionToFileVersion)
        } catch (error) {
            console.error('Error getting version history:', error)
            throw error
        }
    },

    /**
     * Restaurar versão específica
     */
    async restoreVersion(
        agentId: string,
        filePath: string,
        version: number
    ): Promise<void> {
        try {
            // Ler conteúdo da versão antiga
            const content = await this.readFile(agentId, filePath, version)

            // Criar nova versão com o conteúdo antigo
            await this.writeFile(
                agentId,
                filePath,
                content,
                `Restored from version ${version}`,
                { type: 'user', id: 'user' }
            )
        } catch (error) {
            console.error('Error restoring version:', error)
            throw error
        }
    },

    /**
     * Utilitários
     */
    getFileType(filePath: string): string {
        const ext = filePath.split('.').pop()?.toLowerCase()
        switch (ext) {
            case 'md': return 'markdown'
            case 'json': return 'json'
            case 'yaml':
            case 'yml': return 'yaml'
            default: return 'text'
        }
    },

    async hashContent(content: string): Promise<string> {
        const encoder = new TextEncoder()
        const data = encoder.encode(content)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    }
}

// Helper to map DB version to FileVersion
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDBVersionToFileVersion(dbVersion: any): FileVersion {
    return {
        id: dbVersion.id,
        fileId: dbVersion.file_id,
        version: dbVersion.version,
        storagePath: dbVersion.storage_path,
        contentHash: dbVersion.content_hash,
        changedByType: (dbVersion.changed_by_type || 'user') as 'user' | 'agent' | 'system',
        changedById: dbVersion.changed_by_id || '',
        changeMessage: dbVersion.change_message,
        createdAt: new Date(dbVersion.created_at)
    }
}
